import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileSignature, FileText, Loader2, Lock, CheckCircle2, Minus, Plus } from "lucide-react";
import { ContractRegistration } from "@/components/agreement/ContractRegistration";
import { myContract, myContractFields, mySignContract, myContractPdf, myContractPages, myHorseDetails, feeLabel, formatPhone, errMsg } from "@/lib/applyApi";
import { PdfViewer } from "@/components/PdfViewer";
import { ContractPill } from "@/components/AgreementAdmin";
import { SignaturePad } from "@/components/SignaturePad";
import { validWeeks } from "@/components/AdvertisingTermsPicker";
import { TextField, Field, AddressFields, YesNo, validateAddress, validateRegistration } from "@/components/apply/Steps";
import { GenderSelect } from "@/components/adtemplate/GenderSelect";
import { TrainerReference } from "@/components/apply/TrainerFields";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { BrandName } from "@/lib/brand";
import { fmtMT } from "@/lib/mountainTime";

const money = (n) => `$${Number(n).toLocaleString("en-US")}`;
const weeksOf = (f, k) => f?.advertising_weeks ?? String(k.weeks || 1);
const REQUIRED = ["name", "email", "phone", "address", "country", "horse_registered_name", "horse_registered_number", "reserve_amount", "advertising_weeks", "barn_name", "gender", "currency", "gst_added"];

function draftMissing(f) {
  return REQUIRED.filter((key) => {
    if (key === "advertising_weeks") return !validWeeks(f.advertising_weeks);
    if ((key === "horse_registered_name" || key === "horse_registered_number") && f.registered === "no") return false;
    return !String(f[key] || "").trim();
  });
}
const REQ = "Required to sign";
export const isCanada = (c) => ["canada", "ca"].includes(String(c || "").trim().toLowerCase());

function Summary({ k, weeks, country }) {
  const ok = validWeeks(weeks);
  const gst = isCanada(country) ? " + GST" : "";
  return (
    <div data-testid="agreement-summary" className="grid grid-cols-3 gap-2 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm">
      <div><div className="text-[11px] uppercase tracking-wider text-amber-800/70 font-semibold">Advertising Fee</div><div data-testid="seller-agreement-fee" className="font-bold text-amber-950">{feeLabel(k.terms.advertising_fee_percent)}{gst && " + applicable GST"}</div></div>
      <div><div className="text-[11px] uppercase tracking-wider text-amber-800/70 font-semibold">Advertising Weeks</div><div data-testid="summary-weeks" className="font-bold text-amber-950">{ok ? weeks : "—"}</div></div>
      <div><div className="text-[11px] uppercase tracking-wider text-amber-800/70 font-semibold">Listing Fee</div><div data-testid="summary-listing-fee" className="font-bold text-amber-950">{ok ? `${money(Number(weeks) * k.listing_fee_per_week)}${gst}` : "—"}</div></div>
    </div>
  );
}

function Choice({ id, label, value, options, onChange, error }) {
  return (
    <Field id={id} label={label} required error={error}>
      <div role="radiogroup" aria-labelledby={`${id}-label`} className="flex gap-2">
        {options.map((o) => (
          <button key={o} type="button" role="radio" aria-checked={value === o} data-testid={`${id}-${o}`} onClick={() => onChange(o)}
            className={`h-11 px-5 rounded-lg border text-sm font-semibold ${value === o ? "bg-amber-800 border-amber-800 text-white" : "bg-white border-stone-300 text-stone-700"}`}>{o}</button>
        ))}
      </div>
    </Field>
  );
}

function WeeksField({ value, onChange, perWeek, gst }) {
  const n = Number(value) || 0;
  const step = (d) => onChange(String(Math.min(52, Math.max(1, n + d))));
  return (
    <Field id="agr-weeks" label="Number of weeks to advertise" required error={validWeeks(value) ? null : "Enter a whole number from 1 to 52."}
      hint={validWeeks(value) ? `${n} week${n === 1 ? "" : "s"} × $${perWeek} = ${money(n * perWeek)}${gst ? " + GST" : ""} Total Listing Fee` : null}>
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="icon" aria-label="Fewer weeks" data-testid="agr-weeks-minus" onClick={() => step(-1)} disabled={n <= 1} className="h-12 w-12 rounded-full"><Minus className="h-4 w-4" /></Button>
        <Input id="agr-weeks" data-testid="agr-weeks" inputMode="numeric" value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 2))} className="h-12 w-20 text-center text-base" />
        <Button type="button" variant="outline" size="icon" aria-label="More weeks" data-testid="agr-weeks-plus" onClick={() => step(1)} disabled={n >= 52} className="h-12 w-12 rounded-full"><Plus className="h-4 w-4" /></Button>
      </div>
    </Field>
  );
}

const COUNTRY_NAME = { CA: "Canada", US: "United States" };
const hasErr = (o) => Object.keys(o).length > 0;
// Contract-field view of the inline address/registration so required checks work before saving.
const mergeDet = (f, det) => ({ ...f, address: hasErr(validateAddress(det)) ? "" : "ok", country: COUNTRY_NAME[det.country] || f.country,
  registered: det.registered, horse_registered_name: det.registered_name, horse_registered_number: det.registered_number });

const REGION_LABEL = (det) => [det.street_address, [det.city, det.region, (det.postal_code || "").toUpperCase()].filter(Boolean).join(", ")].filter(Boolean).join("\n");
// Live agreement text preview using the unsaved inline details.
const mergeDetText = (f, det, k) => ({ ...f, address: hasErr(validateAddress(det)) ? k.fields.address : REGION_LABEL(det), country: COUNTRY_NAME[det.country] || f.country,
  registered: det.registered, horse_registered_name: det.registered_name, horse_registered_number: det.registered_number });

function HorseSellerDetails({ det, setDet, errors }) {
  return (
    <div data-testid="agreement-horse-seller-details" className="sm:col-span-2 rounded-lg border border-stone-200 bg-stone-50 p-3 space-y-4">
      <span className="text-sm font-semibold text-stone-800">Horse &amp; Seller Details</span>
      <AddressFields v={det} set={setDet} errors={errors} prefix="agr" />
    </div>
  );
}

function RegistrationFields({ det, setDet, setF, errors, appId, onGate, formGender }) {
  return (
    <div data-testid="agreement-registration" className="sm:col-span-2 rounded-lg border border-stone-200 bg-stone-50 p-3 space-y-4">
      <span className="text-sm font-semibold text-stone-800">Horse Registration</span>
      <YesNo id="agr-registered" label="Is this horse registered?" value={det.registered} error={errors.registered}
        onChange={(x) => setDet({ ...det, registered: x, ...(x === "no" ? { registered_name: "", registered_number: "", registration_confirmed: false } : {}) })} />
      {det.registered === "yes" && <ContractRegistration appId={appId} det={det} setDet={setDet} setF={setF} errors={errors} onGate={onGate} formGender={formGender} />}
      {det.registered === "no" && <p data-testid="agr-not-registered-note" className="text-xs text-stone-600">The agreement will show “Not Registered”.</p>}
    </div>
  );
}

function DetailsForm({ k, f, setF, det, setDet, detErrors, dirty, saveAll, bad = [], appId, onRefresh }) {
  const err = (key) => (bad.includes(key) ? REQ : null);
  const [busy, setBusy] = useState(false);
  const set = (p) => setF((s) => ({ ...s, ...p }));
  const one = (key) => (v) => set({ [key]: v });
  const [regGate, setRegGate] = useState(false);
  const horseLocked = det.registered === "yes" && regGate;
  const save = async () => {
    setBusy(true);
    try { if (await saveAll()) toast.success("Agreement details saved"); }
    catch (e) { toast.error(errMsg(e, "Could not save details")); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="agreement-details-form" className="space-y-3">
      <WeeksField value={f.advertising_weeks} onChange={one("advertising_weeks")} perWeek={k.listing_fee_rates?.[f.currency] ?? k.listing_fee_per_week} gst={isCanada(COUNTRY_NAME[det.country] || f.country)} />
      <div className="grid sm:grid-cols-2 gap-3">
        <TextField id="agr-name" label="Name" required value={f.name} onChange={one("name")} autoComplete="name" error={err("name")} />
        <TextField id="agr-phone" label="Phone Number" required type="tel" inputMode="tel" value={f.phone} onChange={one("phone")} error={err("phone")}
          onBlur={() => set({ phone: formatPhone(f.phone) })} autoComplete="tel" />
        <HorseSellerDetails det={det} setDet={setDet} errors={detErrors} />
        <RegistrationFields det={det} setDet={setDet} setF={setF} errors={detErrors} appId={appId} onGate={setRegGate} formGender={f.gender} />
        <TextField id="agr-barn_name" label="Horse's Barn Name" required value={f.barn_name} onChange={one("barn_name")} error={err("barn_name")} disabled={horseLocked}
          hint={horseLocked ? "Available after your registration papers are scanned and confirmed." : undefined} />
        <Field id="agr-gender" label="Gender" required error={err("gender")} hint={horseLocked ? "Filled in from your registration papers." : undefined}>
          <GenderSelect id="agr-gender" value={f.gender} onChange={one("gender")} error={err("gender")} className="h-12" disabled={horseLocked} />
        </Field>
        <TextField id="agr-reserve_amount" label="Reserve Amount ($)" required value={f.reserve_amount} inputMode="numeric" error={err("reserve_amount")}
          onChange={(v) => set({ reserve_amount: v.replace(/\D/g, "").slice(0, 9) })} hint="The minimum price you'll accept (clause 2(c)) — not your expected selling price." />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Choice id="agr-currency" label="Currency of Sale" value={f.currency} options={["CAD", "USD"]} onChange={one("currency")} error={err("currency")} />
        <Choice id="agr-gst" label="GST added onto sale price" value={f.gst_added} options={["Yes", "No"]} onChange={one("gst_added")} error={err("gst_added")} />
      </div>
      <p className="text-xs text-stone-500">Email (<span data-testid="agr-email">{f.email}</span>) and the Advertising Fee are set by <BrandName />. We filled in what we already had on file — please correct anything that's out of date.</p>
      <Button data-testid="agreement-save-details" onClick={save} disabled={busy || !dirty || !validWeeks(f.advertising_weeks)} className="h-11 rounded-full bg-primary hover:bg-amber-900 text-white">
        {dirty ? "Save details" : "Details saved"}
      </Button>
    </div>
  );
}

function AgreementText({ k, f }) {
  const v = (key) => (key === "advertising_weeks" ? f.advertising_weeks : key === "reserve_amount" ? (f.reserve_amount ? `$ ${f.reserve_amount}` : "") : key === "phone" ? formatPhone(f.phone)
    : (key === "horse_registered_name" || key === "horse_registered_number") && f.registered === "no" ? "Not Registered" : f[key]);
  return (
    <div data-testid="agreement-text" tabIndex={0} aria-label="Agreement text" className="max-h-[60vh] overflow-y-auto rounded-lg border border-stone-200 bg-white p-4 text-sm leading-relaxed text-stone-800 space-y-2">
      <h3 className="text-center font-bold text-base">{k.title}</h3>
      <p>{k.intro}</p>
      <div>{k.company_lines.map((l, i) => <div key={l} className={i === 0 ? "font-semibold" : ""}>{l}</div>)}</div>
      <dl className="grid grid-cols-[minmax(0,180px)_1fr] gap-x-3 gap-y-0.5">
        {k.field_labels.map(([key, label]) => (
          <div key={key} className="contents"><dt className="font-semibold">{label}</dt><dd data-testid={`agreement-field-${key}`} className={v(key) ? "" : "text-red-600 italic"}>{v(key) || "Missing"}</dd></div>
        ))}
      </dl>
      {k.clauses.map((c) => (
        <section key={c.number}>
          <h4 className="font-bold mt-3">{c.number} {c.heading}</h4>
          {c.paragraphs.map((p, i) => <p key={i} className="whitespace-pre-wrap mt-1">{p}</p>)}
        </section>
      ))}
    </div>
  );
}

function SignForm({ k, f, det, appId, consentText, onSigned, saveAll, onMissing }) {
  const [type, setType] = useState("drawn");
  const [name, setName] = useState(k.fields.name || "");
  const [drawn, setDrawn] = useState("");
  const [typed, setTyped] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState([]);
  const label = Object.fromEntries(k.field_labels);
  const sign = async () => {
    const miss = draftMissing(mergeDet(f, det));
    const sig = [];
    if (name.trim().length < 2) sig.push("Your full legal name");
    if (type === "drawn" ? !drawn : typed.trim().length < 2) sig.push("Signature");
    if (!consent) sig.push("Electronic signing consent");
    onMissing(miss);
    const list = [...miss.map((x) => label[x] || x), ...sig];
    setProblems(list);
    if (list.length) { toast.error(`Please complete: ${list.join(", ")}`); return; }
    setBusy(true);
    try {
      const cur = await saveAll();
      if (!cur) return;
      if (cur.missing.length) { onMissing(cur.missing); setProblems(cur.missing.map((x) => label[x] || x)); return; }
      const res = await mySignContract(appId, k.id, { signer_name: name, signature_type: type, signature_data: type === "drawn" ? drawn : typed, consent, text_sha256: cur.text_sha256 });
      toast.success("Agreement signed — opening your Horse Ad Template");
      onSigned(res);
    } catch (e) { toast.error(errMsg(e, "Could not sign")); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="agreement-sign-form" className="space-y-3">
      {problems.length > 0 && (
        <div data-testid="agreement-missing" role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <strong>Please complete before signing:</strong> {problems.join(", ")}
        </div>
      )}
      <TextField id="agr-signer-name" label="Your full legal name" required value={name} onChange={setName} autoComplete="name" />
      <div role="radiogroup" aria-label="Signature method" className="flex gap-2">
        {[["drawn", "Draw signature"], ["typed", "Type signature"]].map(([val, l]) => (
          <button key={val} type="button" role="radio" aria-checked={type === val} data-testid={`sign-type-${val}`} onClick={() => setType(val)}
            className={`h-10 px-4 rounded-full border text-sm font-semibold ${type === val ? "bg-amber-800 border-amber-800 text-white" : "bg-white border-stone-300 text-stone-700"}`}>{l}</button>
        ))}
      </div>
      {type === "drawn" ? <SignaturePad onChange={setDrawn} /> : (
        <TextField id="agr-typed-signature" label="Type your signature" value={typed} onChange={setTyped} className="h-14 text-2xl italic" />
      )}
      <label className="flex items-start gap-3 cursor-pointer">
        <Checkbox data-testid="agreement-consent" checked={consent} onCheckedChange={(val) => setConsent(!!val)} className="mt-0.5 h-5 w-5" />
        <span className="text-sm text-stone-700">{consentText}</span>
      </label>
      <Button data-testid="agreement-sign" onClick={sign} disabled={busy} className="w-full h-12 rounded-full bg-amber-800 hover:bg-amber-900 text-white">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileSignature className="h-4 w-4 mr-1.5" />Sign Agreement</>}
      </Button>
    </div>
  );
}

function OpenAgreement({ k, details, appId, consentText, onUpdate, onSigned, onRefresh, pdf }) {
  const saved = { ...k.fields, advertising_weeks: weeksOf(k.fields, k) };
  const [f, setF] = useState({ ...saved, phone: formatPhone(saved.phone) });
  const savedDet = { street_address: "", city: "", region: "", postal_code: "", registered: "", registered_name: "", registered_number: "", ...details,
    country: ["CA", "US"].includes(details?.country) ? details.country : "" };
  const [det, setDet] = useState(savedDet);
  const [detTried, setDetTried] = useState(false);
  const [bad, setBad] = useState([]);
  const initF = { ...saved, phone: formatPhone(saved.phone) };
  const fDirty = JSON.stringify(f) !== JSON.stringify(initF);
  const detDirty = JSON.stringify(det) !== JSON.stringify(savedDet);
  const dirty = fDirty || detDirty;
  const showDet = bad.some((x) => ["address", "country", "horse_registered_name", "horse_registered_number"].includes(x));
  const detErrors = showDet ? { ...validateAddress(det), ...validateRegistration(det) } : detTried ? detCheck() : {};
  const ADDR_KEYS = ["street_address", "city", "region", "postal_code", "country"];
  const REG_KEYS = ["registered", "registered_name", "registered_number"];
  const changed = (keys) => keys.some((x) => (det[x] || "") !== (savedDet[x] || ""));
  const detCheck = () => ({ ...(changed(ADDR_KEYS) ? validateAddress(det) : {}), ...(changed(REG_KEYS) ? validateRegistration(det) : {}) });
  const saveAll = async () => {
    if (detDirty && hasErr(detCheck())) {
      setDetTried(true);
      toast.error("Please complete the highlighted address or registration details");
      return null;
    }
    let cur = k;
    if (fDirty) cur = await myContractFields(appId, k.id, { ...f, ...(COUNTRY_NAME[det.country] ? { country: COUNTRY_NAME[det.country] } : {}) });
    if (detDirty) { const res = await myHorseDetails(appId, det); onRefresh(res); return res.current; }
    if (fDirty) { onUpdate(cur); setF({ ...cur.fields, advertising_weeks: weeksOf(cur.fields, cur) }); }
    return cur;
  };
  return (
    <>
      <Summary k={k} weeks={f.advertising_weeks} country={f.country} />
      {k.kind === "amendment" && <p className="text-sm text-violet-800">This is an amended agreement. Your previously signed agreement stays in force until you sign this one.</p>}
      <div><h3 className="font-semibold text-stone-900 mb-2">1. Complete or confirm your details</h3>
        <DetailsForm k={k} f={f} setF={setF} det={det} setDet={setDet} detErrors={detErrors} dirty={dirty} bad={bad} saveAll={saveAll} appId={appId} onRefresh={onRefresh} /></div>
      <div><h3 className="font-semibold text-stone-900 mb-2">2. Review the complete agreement</h3><AgreementText k={k} f={mergeDetText(f, det, k)} />
        <button type="button" data-testid="agreement-draft-pdf" onClick={() => pdf(k.id)} className="mt-2 text-sm text-amber-800 underline inline-flex items-center gap-1"><FileText className="h-4 w-4" />View as PDF (draft)</button></div>
      <div><h3 className="font-semibold text-stone-900 mb-2">3. Sign</h3><SignForm key={k.id} k={k} f={f} det={det} appId={appId} consentText={consentText} onSigned={onSigned} saveAll={saveAll} onMissing={setBad} /></div>
    </>
  );
}

export function AgreementSeller({ appId }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const onSigned = (res) => {
    setD(res);
    if (res?.status === "signed" && res.horse_id) {
      try { navigate(`/listing/${res.horse_id}`); } catch { /* fallback button below */ }
    }
  };
  const load = useCallback(() => myContract(appId).then(setD).catch(() => setD(null)), [appId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (d && window.location.hash === "#agreement") document.getElementById("agreement")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [d]);
  const [viewing, setViewing] = useState(null);
  if (!d || d.status === "not_started") return null;
  const k = d.current;
  const pdf = (cid) => setViewing(cid);
  return (
    <section id="agreement" data-testid="agreement-seller" className="bg-white border border-stone-200 rounded-xl p-4 space-y-4 scroll-mt-20">
      <div className="flex items-center justify-between gap-2">
        <h2 data-testid="seller-agreement-title" className="font-semibold text-stone-900 flex items-center gap-2"><FileSignature className="h-4 w-4 shrink-0 text-amber-800" /> {k?.document_name || "Horse Sale Advertising Agreement"}</h2>
        <span className="flex items-center gap-1.5">{k?.is_test && <span data-testid="seller-agreement-test-badge" className="rounded bg-fuchsia-600 text-white text-[10px] font-extrabold px-1.5 py-0.5">TEST</span>}<ContractPill status={d.status} testId="seller-agreement-status" /></span>
      </div>
      <TrainerReference t={d.trainer} />
      {k.status === "awaiting_seller" ? (
        <OpenAgreement key={k.id + k.text_sha256} k={k} details={d.details} appId={appId} consentText={d.consent_text} pdf={pdf} onSigned={onSigned} onRefresh={setD}
          onUpdate={(nk) => setD((s) => ({ ...s, current: nk }))} />
      ) : (
        <>
          <Summary k={k} weeks={String(k.weeks)} />
          <div className="text-sm text-emerald-800 flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5" /><span>Signed by {k.signed?.signer_name} on {fmtMT(k.signed?.signed_at)}. <Lock className="inline h-3.5 w-3.5" /> Locked — changes require a new signed amendment.</span></div>
          {d.horse_id && (
            <Button data-testid="continue-ad-template" onClick={() => navigate(`/listing/${d.horse_id}`)} className="w-full sm:w-auto h-12 rounded-full bg-amber-800 hover:bg-amber-900 text-white font-bold">
              Continue to Horse Ad Template
            </Button>
          )}
        </>
      )}
      {d.signed_history.length > 0 && (
        <ul data-testid="seller-signed-agreements" className="space-y-1 text-sm">
          {d.signed_history.map((s) => (
            <li key={s.id}><button type="button" data-testid={`seller-signed-pdf-${s.version}`} onClick={() => pdf(s.id)} className="text-amber-800 underline inline-flex items-center gap-1">
              <FileText className="h-4 w-4" /><span data-testid={`seller-signed-name-${s.version}`}>{s.document_name}</span> · v{s.version} ({feeLabel(s.terms.advertising_fee_percent)}){s.status === "superseded" && " — superseded"}</button></li>
          ))}
        </ul>
      )}
      <PdfViewer open={!!viewing} onClose={() => setViewing(null)} loadPages={() => myContractPages(appId, viewing)} loadPdf={() => myContractPdf(appId, viewing)} />
    </section>
  );
}
